import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import swal from "sweetalert";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import "../supplier/supplierManagementTables.css";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import {
  cardSurface,
  cardBody,
  cardHeader,
  cardHeaderTitle,
  dangerBtn,
  detailLabel,
  detailValue,
  primaryBtn,
  secondaryBtn,
  statusBadge,
  tableCardSurface,
  thStyle,
} from "./assessmentStyles";

const IS_SUPPLIER_INSTANCE = process.env.REACT_APP_INSTANCE_TYPE === "supplier";

const STATUS_VARIANT = {
  DRAFT: "warning",
  PUBLISHED: "success",
  ARCHIVED: "muted",
  ASSIGNED: "info",
  IN_PROGRESS: "warning",
  SUBMITTED: "success",
  REVIEWED: "success",
  CANCELLED: "muted",
};

/**
 * Pull an assignment out of the /by-supplier/:supplierId/:id response —
 * the backend hasn't pinned a shape yet, so probe the common ones:
 * a bare assignment, {assignment}, {assignments: [...]}, or an array.
 */
const pickAssignment = (raw, assignmentId) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    if (assignmentId) {
      const match = raw.find((a) => Number(a?.id) === Number(assignmentId));
      if (match) return match;
    }
    return raw[0] ?? null;
  }
  if (raw.assignment) return raw.assignment;
  if (Array.isArray(raw.assignments)) {
    if (assignmentId) {
      const match = raw.assignments.find(
        (a) => Number(a?.id) === Number(assignmentId)
      );
      if (match) return match;
    }
    return raw.assignments[0] ?? null;
  }
  if (raw.status_key && raw.assessment_id) return raw;
  return null;
};

/**
 * Same defensive probe for the assessment side of the response — the
 * endpoint may either return the assessment at the top level, nested
 * under .assessment, or omit it entirely (in which case the caller
 * falls back to getAssessment).
 */
const pickAssessment = (raw) => {
  if (!raw) return null;
  if (raw.assessment && typeof raw.assessment === "object") {
    return raw.assessment;
  }
  if (raw.kpis || raw.title || raw.owner_type_key) return raw;
  return null;
};

const ViewAssessment = () => {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();

  const assessmentIdParam = useMemo(() => {
    const n = Number(params.assessmentId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params.assessmentId]);
  const assignmentIdParam = useMemo(() => {
    const n = Number(params.assignmentId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params.assignmentId]);

  const sourceParam = useMemo(() => {
    const q = new URLSearchParams(location.search).get("source");
    return q === "riu_backend" ? "riu_backend" : "company";
  }, [location.search]);

  const [assessment, setAssessment] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const [industryNames, setIndustryNames] = useState({});
  const [subIndustryNames, setSubIndustryNames] = useState({});
  const [referencedTemplateTitle, setReferencedTemplateTitle] = useState("");

  const isOwnedByCompany = assessment?.owner_type_key === "COMPANY_CUSTOM";
  const isDraft = assessment?.status_key === "DRAFT";
  const isPublished = assessment?.status_key === "PUBLISHED";
  const isRecipientView = Boolean(assignmentIdParam);
  const canEdit =
    !IS_SUPPLIER_INSTANCE &&
    !isRecipientView &&
    isOwnedByCompany &&
    (isDraft || isPublished);
  const canDelete =
    !IS_SUPPLIER_INSTANCE && !isRecipientView && isOwnedByCompany && isDraft;

  const handleDelete = async () => {
    if (!assessment?.id) return;
    const confirmed = await swal({
      title: "Delete this draft?",
      text: `"${assessment.title}" will be removed. This cannot be undone.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirmed) return;
    setDeleting(true);
    const res = await AssessmentService.deleteAssessment(assessment.id, "company");
    setDeleting(false);
    if (res?.isSuccess || res?.status === 204) {
      await swal({ title: "Deleted", icon: "success" });
      history.push("/assessment/all");
    } else {
      await swal({
        title: "Delete failed",
        text: res?.message || "Please try again.",
        icon: "error",
      });
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const assessmentPromise = assignmentIdParam
        ? AssessmentService.getByAssignment(assignmentIdParam)
        : AssessmentService.getAssessment(assessmentIdParam, sourceParam);
      const [aRes, iRes] = await Promise.all([
        assessmentPromise,
        AssessmentService.listIndustries(),
      ]);
      setLoading(false);
      if (aRes?.isSuccess) {
        const raw = aRes?.data?.data ?? null;
        if (assignmentIdParam) {
          const parsedAssignment = pickAssignment(raw, assignmentIdParam);
          const parsedAssessment = pickAssessment(raw);
          setAssignment(parsedAssignment);
          if (parsedAssessment) {
            setAssessment(parsedAssessment);
          } else if (parsedAssignment?.assessment_id) {
            const fallbackSource = parsedAssignment.company_id
              ? "company"
              : "riu_backend";
            const fallback = await AssessmentService.getAssessment(
              parsedAssignment.assessment_id,
              fallbackSource
            );
            if (fallback?.isSuccess) setAssessment(fallback?.data?.data ?? null);
          }
        } else {
          setAssessment(raw);
        }
      }
      const currentAssessment =
        assignmentIdParam && aRes?.isSuccess
          ? pickAssessment(aRes?.data?.data ?? null)
          : aRes?.data?.data;
      if (currentAssessment?.referenced_assessment_id) {
        AssessmentService.getRiuTemplateKpis(
          currentAssessment.referenced_assessment_id
        ).then((tplRes) => {
          if (tplRes?.isSuccess) {
            setReferencedTemplateTitle(tplRes?.data?.data?.title || "");
          }
        });
      }
      const industryList = iRes?.data?.data ?? iRes?.data ?? [];
      if (Array.isArray(industryList)) {
        const indMap = {};
        const subMap = {};
        industryList.forEach((row) => {
          indMap[row.industry_type_id] = row.industry_type_name;
          (row.items || []).forEach((sub) => {
            subMap[sub.id] = sub.title;
          });
        });
        setIndustryNames(indMap);
        setSubIndustryNames(subMap);
      }
    })();
  }, [assessmentIdParam, assignmentIdParam]);

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle={assessment ? assessment.title : "Assessment"}
    >
      <div style={cardSurface}>
        <div style={cardHeader}>
          <h5 style={cardHeaderTitle}>Details</h5>
          <div style={{ display: "flex", gap: 8 }}>
            {canEdit && (
              <button
                type="button"
                style={primaryBtn}
                onClick={() =>
                  history.push(`/assessment/edit/${assessment.id}`)
                }
              >
                <i className="far fa-edit me-1"></i> Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                style={dangerBtn}
                onClick={handleDelete}
                disabled={deleting}
              >
                <i className="far fa-trash-alt me-1"></i>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}
            <button
              type="button"
              style={secondaryBtn}
              onClick={() => history.goBack()}
            >
              Back
            </button>
          </div>
        </div>
        <div style={cardBody}>
          {loading ? (
            <div className="text-center text-muted py-4">Loading…</div>
          ) : !assessment ? (
            <div className="text-center text-muted py-4">
              Assessment not found.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 18,
              }}
            >
              <div>
                <div style={detailLabel}>Status</div>
                <div style={detailValue}>
                  {(() => {
                    const displayStatus =
                      assignment?.status_key || assessment.status_key;
                    return (
                      <span
                        style={statusBadge(
                          STATUS_VARIANT[displayStatus] || "info"
                        )}
                      >
                        {displayStatus}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div>
                <div style={detailLabel}>Industry</div>
                <div style={detailValue}>
                  {industryNames[assessment.industry] || assessment.industry}
                </div>
              </div>
              <div>
                <div style={detailLabel}>Sub-industry</div>
                <div style={detailValue}>
                  {subIndustryNames[assessment.sub_industry] ||
                    assessment.sub_industry}
                </div>
              </div>
              <div>
                <div style={detailLabel}>Updated</div>
                <div style={detailValue}>
                  {assessment.updated_at?.slice(0, 10)}
                </div>
              </div>
              {assessment.description && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={detailLabel}>Description</div>
                  <div style={detailValue}>{assessment.description}</div>
                </div>
              )}
              {assessment.referenced_assessment_id && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={detailLabel}>Based on RIU template</div>
                  <div style={detailValue}>
                    {referencedTemplateTitle
                      ? referencedTemplateTitle
                      : `#${assessment.referenced_assessment_id}`}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {(() => {
        const renderKpiRow = (k, idx, isChild) => {
          const src = k.source?.type || null;
          const showSourceBadge = src && !assignmentIdParam;
          const effectiveWeightage =
            k.weightage != null
              ? k.weightage
              : k.default_weightage != null
              ? k.default_weightage
              : null;
          const isOverridden =
            k.weightage_override != null && k.weightage_override !== k.default_weightage;
          return (
            <tr key={`${isChild ? "c" : "p"}-${src || "riu"}:${k.id ?? k.kpi_id}`}>
              <td>{isChild ? "" : idx + 1}</td>
              <td style={{ paddingLeft: isChild ? 32 : undefined }}>
                <div>
                  {isChild ? "↳ " : ""}
                  {k.kpi_title || "(unknown KPI)"}
                </div>
                {showSourceBadge && (
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: 4,
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      padding: "2px 8px",
                      borderRadius: 4,
                      color: src === "company" ? "#065f46" : "#1e40af",
                      background: src === "company" ? "#d1fae5" : "#dbeafe",
                    }}
                  >
                    {src === "company" ? "MY KPI" : "RIU"}
                  </span>
                )}
              </td>
              <td>{k.is_mandatory ? "Yes" : "No"}</td>
              <td
                title={
                  isOverridden
                    ? `Override (default: ${k.default_weightage ?? "—"})`
                    : "Default weightage"
                }
              >
                {effectiveWeightage != null ? (
                  <span style={{ fontWeight: isOverridden ? 700 : 400 }}>
                    {effectiveWeightage}
                    {isOverridden ? "*" : ""}
                  </span>
                ) : (
                  <span style={{ color: "var(--ink-4)" }}>—</span>
                )}
              </td>
            </tr>
          );
        };
        const totalCount = (assessment?.kpis || []).reduce(
          (acc, k) => acc + 1 + (Array.isArray(k.sub_kpis) ? k.sub_kpis.length : 0),
          0
        );
        return (
          <div style={{ ...cardSurface, marginTop: 18 }}>
            <div style={cardHeader}>
              <h5 style={cardHeaderTitle}>KPIs ({totalCount})</h5>
              {assessment?.kpis?.length > 0 && assignmentIdParam && (
                <button
                  type="button"
                  style={primaryBtn}
                  onClick={() =>
                    history.push(
                      `/assessment/assignment/${assignmentIdParam}/take`
                    )
                  }
                >
                  {IS_SUPPLIER_INSTANCE ? "Start Assessment" : "View Assessment"}
                </button>
              )}
            </div>
            <div style={tableCardSurface}>
              <div className="supplier-mgmt-table-scroll">
                <table className="supplier-mgmt-table table">
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 50 }}>#</th>
                      <th style={thStyle}>KPI</th>
                      <th style={{ ...thStyle, width: 140 }}>Mandatory</th>
                      <th style={{ ...thStyle, width: 110 }}>Weightage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assessment?.kpis && assessment.kpis.length > 0 ? (
                      assessment.kpis.flatMap((parent, idx) => [
                        renderKpiRow(parent, idx, false),
                        ...(Array.isArray(parent.sub_kpis)
                          ? parent.sub_kpis.map((c) => renderKpiRow(c, idx, true))
                          : []),
                      ])
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          No KPIs in this assessment.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}
    </SupplierManagementShell>
  );
};

export default ViewAssessment;
